import clsx from "clsx";
import React, { useState, Dispatch, SetStateAction, useContext } from "react";
import {
  Button as CoreButton,
  ButtonProps as CoreButtonProps,
} from "../button";
import classes from "../button/button.module.css";

export type AsyncActionStatus = "idle" | "loading" | "success" | "error";

export type AsyncActionContextValue = {
  status: AsyncActionStatus;
  setStatus: Dispatch<SetStateAction<AsyncActionStatus>>;
  successTimeoutMs?: number;
};

const AsyncActionContext = React.createContext<AsyncActionContextValue>({
  status: "idle",
  setStatus: () => {},
});

type RootProps = {
  successTimeoutMs?: number;
  children: React.ReactNode;
};

export const Root = ({ successTimeoutMs, children }: RootProps) => {
  const [status, setStatus] = useState<AsyncActionStatus>("idle");

  return (
    <AsyncActionContext.Provider
      value={{ status, setStatus, successTimeoutMs }}
    >
      {children}
    </AsyncActionContext.Provider>
  );
};

Root.displayName = "AsyncActionRoot";

type ButtonProps = Omit<CoreButtonProps, "onClick"> & {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => Promise<boolean>;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ onClick, alignment = "center", className = "", ...props }, ref) => {
    const { status, setStatus, successTimeoutMs } =
      useContext(AsyncActionContext);

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (status === "loading" || status === "success") {
        return;
      }

      setStatus("loading");

      const result = await onClick(e);

      if (result) {
        if (successTimeoutMs === 0) {
          setStatus("idle");
          return;
        }

        setStatus("success");

        if (!successTimeoutMs) return;

        const successTimeoutId = setTimeout(() => {
          setStatus("idle");
          clearTimeout(successTimeoutId);
        }, successTimeoutMs);

        return;
      }

      setStatus("error");
    };

    return (
      <CoreButton
        ref={ref}
        onClick={handleClick}
        className={clsx(
          status === "success" && classes.successStatus,
          className,
        )}
        {...props}
        alignment={alignment}
      />
    );
  },
);

Button.displayName = "AsyncActionButton";

type AsyncActionStatusProps = {
  children: React.ReactNode;
};

export const Idle = ({ children }: AsyncActionStatusProps) => {
  const { status } = useContext(AsyncActionContext);

  if (status !== "idle") return null;

  return children;
};

Idle.displayName = "AsyncActionIdle";

export const Loading = ({ children }: AsyncActionStatusProps) => {
  const { status } = useContext(AsyncActionContext);

  if (status !== "loading") return null;

  return children;
};

Loading.displayName = "AsyncActionLoading";

export const Success = ({ children }: AsyncActionStatusProps) => {
  const { status } = useContext(AsyncActionContext);

  if (status !== "success") return null;

  return children;
};

Success.displayName = "AsyncActionSuccess";

export const Error = ({ children }: AsyncActionStatusProps) => {
  const { status } = useContext(AsyncActionContext);

  if (status !== "error") return null;

  return children;
};

Error.displayName = "AsyncActionError";
